import { useEffect, useRef, useState } from "react";
import { api, type Role } from "../api";
import { forgetPin, savedPin, savePin } from "../rememberedPin";

/**
 * First-time gate: the whole app sits behind the admin PIN. The server sets a
 * long-lived cookie on success, so this appears once per browser (and again if
 * the cookie is gone or the PIN was changed).
 */
export default function PinGate({ onDone }: { onDone: (role: Role) => void }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // A PIN this phone kept is tried once, so nobody has to type it again. One the server turns down is forgotten;
  // without a connection it is kept for the next try.
  const triedSaved = useRef(false);
  useEffect(() => {
    if (triedSaved.current) return;
    triedSaved.current = true;
    const saved = savedPin();
    if (!saved) {
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    api
      .verifyPin(saved)
      .then(({ role }) => onDone(role))
      .catch((err) => {
        if (!(err instanceof TypeError)) forgetPin();
        setError(err instanceof TypeError ? "No connection. Check the network and try again." : "The PIN saved on this phone no longer works. Enter the PIN.");
        setBusy(false);
        inputRef.current?.focus();
      });
  }, [onDone]);

  const submit = async () => {
    if (pin.length !== 4 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { role } = await api.verifyPin(pin);
      savePin(pin);
      onDone(role);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not check the PIN");
      setPin("");
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 px-6">
      <img src="/logo.png" alt="Soulcake" className="h-16 w-auto" />
      <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#3a1c25]">Enter PIN</h1>
      <p className="-mt-3 text-center text-sm font-semibold text-ink/60">
        First time on this phone? Ask Jonas{" "}
        <a href="tel:+4741261013" className="inline-flex items-center gap-1 whitespace-nowrap font-bold text-hot">
          <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
          </svg>
          41261013
        </a>{" "}
        or management for the PIN. You only need it once.
      </p>
      <form
        className="flex w-full max-w-xs flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          aria-label="PIN"
          className={`h-16 w-full rounded-[14px] bg-white text-center outline-none placeholder:text-ink/30 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20 ${
            pin ? "text-4xl font-bold tracking-[0.5em]" : "text-2xl"
          }`}
        />
        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
        <button
          type="submit"
          disabled={pin.length !== 4 || busy}
          className="h-14 w-full rounded-[14px] bg-rose-grad text-lg font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-50 shadow-button"
        >
          {busy ? "Checking…" : "Open Status App"}
        </button>
      </form>
    </div>
  );
}
